import { LoadingSpinner } from "@/components/loading-spinner";
import { Button } from "@/components/ui/button";
import { useAssessmentStore } from "@/stores/assessment";
import {
  Alert,
  Fade,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Modal } from "../../../../components";
import services from "../../../../services";
import { useData } from "../use-data";

interface ModalActivateProps {
  open: boolean;
  onClose: () => void;
}

export default function ModalActivate({ open, onClose }: ModalActivateProps) {
  const { refetch, lessons, modules } = useData();
  const { selectedItem } = useAssessmentStore();
  const [selectedModule, setSelectedModule] = useState<string>("");
  const [selectedLesson, setSelectedLesson] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // NOVO: estado local para ativação/desativação
  const [isActive, setIsActive] = useState<boolean>(false);
  const [associationType, setAssociationType] = useState<
    "lesson" | "module" | "level"
  >("lesson");
  const [selectedLevel, setSelectedLevel] = useState<string>("");

  // Sincroniza o estado local com o selectedItem
  useEffect(() => {
    if (selectedItem) {
      // Considera ativo se status for diferente de PENDING ou isDisabled for false
      setIsActive(
        selectedItem.status !== "PENDING" && selectedItem.status !== "DISABLED"
      );
    } else {
      setIsActive(false);
    }
  }, [selectedItem]);

  // Filtra as aulas do módulo selecionado
  const filteredLessons = lessons?.filter((l) => l.moduleId === selectedModule);

  useEffect(() => {
    if (!open) {
      setSelectedModule("");
      setSelectedLesson("");
      setSelectedLevel("");
      setAssociationType("lesson");
      setError(null);
      setLoading(false);
    }
  }, [open]);

  // Resetar aula ao trocar módulo
  useEffect(() => {
    setSelectedLesson("");
  }, [selectedModule]);

  const resetStates = useCallback(() => {
    setSelectedModule("");
    setSelectedLesson("");
    setError(null);
    setLoading(false);
  }, []);

  const closeModal = useCallback(() => {
    resetStates();
    onClose();
    refetch();
  }, [onClose, refetch, resetStates]);

  // handler para ativar/desativar
  const handleAction = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (!selectedItem) {
        setError("Nenhuma avaliação selecionada.");
        setLoading(false);
        return;
      }

      if (!isActive) {
        // ATIVAR
        let data: any = {};
        if (associationType === "lesson") {
          if (!selectedLesson) {
            setError("Selecione uma aula para ativar a avaliação.");
            setLoading(false);
            return;
          }
          data.lessonId = selectedLesson;
        } else if (associationType === "module") {
          if (!selectedModule) {
            setError("Selecione um módulo para ativar a avaliação.");
            setLoading(false);
            return;
          }
          data.moduleId = selectedModule;
        } else if (associationType === "level") {
          if (!selectedLevel) {
            setError("Selecione um nível para ativar a avaliação.");
            setLoading(false);
            return;
          }
          data.examLevel = Number(selectedLevel);
        }

        await services.api.post(
          `/assessments/${selectedItem.id}/activate`,
          data
        );
        toast.success("Avaliação ativada com sucesso");
        setIsActive(true);
      } else {
        // DESATIVAR
        await services.api.post(
          `/assessments/${selectedItem.id}/deactivate`,
          {}
        );
        toast.success("Avaliação desativada com sucesso");
        setIsActive(false);
      }
      closeModal();
    } catch (err: any) {
      setError(err?.message || "Erro ao processar ação.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      className="w-full max-w-[90%] md:max-w-[80%] lg:max-w-[420px] max-h-[95%]"
      head={
        <div>
          <h2 className="text-xl font-bold">
            {!isActive ? "Ativar Avaliação" : "Desativar Avaliação"}
          </h2>
          {!loading && (
            <p className="text-sm font-medium text-center mt-4 ">
              {!isActive
                ? "Tem certeza que deseja ativar esta avaliação?"
                : "Tem certeza que deseja desativar esta avaliação?"}
            </p>
          )}
        </div>
      }
    >
      <Fade in={open}>
        {loading ? (
          <div className="flex items-center justify-center h-full min-h-[200px] w-full flex-col gap-4">
            <LoadingSpinner />
            <p className="text-sm text-gray-500">
              {!isActive ? "Ativando avaliação..." : "Desativando avaliação..."}
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleAction}
            className="grid gap-6 h-full w-full p-6"
          >
            {!isActive && (
              <>
                {/* Radio para tipo de associação */}
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={associationType}
                    onChange={(e) => setAssociationType(e.target.value as any)}
                  >
                    <FormControlLabel
                      value="lesson"
                      control={<Radio />}
                      label="Aula"
                    />
                    <FormControlLabel
                      value="module"
                      control={<Radio />}
                      label="Módulo"
                    />
                    <FormControlLabel
                      value="level"
                      control={<Radio />}
                      label="Nível"
                    />
                  </RadioGroup>
                </FormControl>
                {/* Se for aula: select de módulo (filtro) e aula */}
                {associationType === "lesson" && (
                  <>
                    <FormControl fullWidth size="small">
                      <InputLabel
                        id="module-select-label"
                        sx={{ color: "#fff" }}
                      >
                        Módulo (filtro)
                      </InputLabel>
                      <Select
                        labelId="module-select-label"
                        value={selectedModule}
                        label=" Módulo (filtro)"
                        onChange={(e) => setSelectedModule(e.target.value)}
                        disabled={loading || !modules}
                      >
                        {modules?.map((module) => (
                          <MenuItem key={module.id} value={module.id}>
                            {module.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      disabled={!selectedModule}
                    >
                      <InputLabel
                        id="lesson-select-label"
                        sx={{ color: "#fff" }}
                      >
                        Aula
                      </InputLabel>
                      <Select
                        labelId="lesson-select-label"
                        value={selectedLesson}
                        label="Aula"
                        onChange={(e) => setSelectedLesson(e.target.value)}
                        disabled={loading || !selectedModule}
                      >
                        {filteredLessons?.map((lesson) => (
                          <MenuItem key={lesson.id} value={lesson.id}>
                            {lesson.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
                {/* Se for módulo: select de módulo */}
                {associationType === "module" && (
                  <FormControl fullWidth size="small" required>
                    <InputLabel id="module-select-label" sx={{ color: "#fff" }}>
                      Módulo
                    </InputLabel>
                    <Select
                      labelId="module-select-label"
                      value={selectedModule}
                      label="Módulo"
                      onChange={(e) => setSelectedModule(e.target.value)}
                      disabled={loading || !modules}
                    >
                      {modules?.map((module) => (
                        <MenuItem key={module.id} value={module.id}>
                          {module.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {/* Se for nível: select de nível */}
                {associationType === "level" && (
                  <FormControl fullWidth size="small" required>
                    <InputLabel id="level-select-label" sx={{ color: "#fff" }}>
                      Nível
                    </InputLabel>
                    <Select
                      labelId="level-select-label"
                      value={selectedLevel}
                      label="Nível"
                      onChange={(e) => setSelectedLevel(e.target.value)}
                      disabled={loading}
                    >
                      <MenuItem value={1}>Nível 1</MenuItem>
                      <MenuItem value={2}>Nível 2</MenuItem>
                      <MenuItem value={3}>Nível 3</MenuItem>
                      <MenuItem value={4}>Nível 4</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </>
            )}
            {error && (
              <Alert severity="error" className="text-sm">
                {error}
              </Alert>
            )}
            <div className="flex items-center justify-end gap-3 mt-4 border-t pt-4">
              <Button
                disabled={loading}
                variant="outline"
                type="button"
                onClick={closeModal}
                className="min-w-[120px] py-3 h-fit"
                size="sm"
              >
                Cancelar
              </Button>
              <Button
                disabled={loading}
                type="submit"
                className="min-w-[120px] py-3 h-fit"
                size="sm"
              >
                {!isActive ? "Ativar Avaliação" : "Desativar Avaliação"}
              </Button>
            </div>
          </form>
        )}
      </Fade>
    </Modal>
  );
}
